// The questions the RD initial assessment asks that no other template codes,
// with their answer sets and how each relates to LOINC. Where LOINC names the
// question exactly, the note records under the LOINC code itself and the
// question is not here: how many times a day the participant eats is 80491-4
// (Meals 24 hour Estimated: a rate in /d, ACTIVE, estimated by report).
//
// Answers are the options the template lists, worded as it words them.
import type { ConceptMapGroupElement, ValueSet } from '@medplum/fhirtypes'
import {
  type AnswerEntry,
  answerConcepts,
  answerValueSet,
  type Capture,
  type TemplateQuestion,
  templateQuestionConcept,
  templateQuestionLoincMap,
  VALUE_SET_BASE,
} from '#src/terminology/define.ts'
import { ASSESSMENT_ANSWERS_SYSTEM } from '#src/terminology/types.ts'

const YES_NO: Capture = {
  kind: 'choice',
  answerSet: `${VALUE_SET_BASE}/yes-no`,
}
const TEXT: Capture = { kind: 'text' }
const choice = (name: string, options?: { repeats?: boolean }): Capture => {
  const capture: Capture = {
    kind: 'choice',
    answerSet: `${VALUE_SET_BASE}/${name}`,
  }
  if (options?.repeats) {
    capture.repeats = true
  }
  return capture
}

const NO_LOINC_EDUCATION =
  'LOINC education terms are instrument items or notes, not this topic'

const EDUCATION_TOPICS: [slug: string, topic: string][] = [
  ['general-wellness', 'General Wellness'],
  ['heart-healthy', 'Heart Healthy'],
  ['carbohydrate-controlled', 'Carbohydrate Controlled'],
  ['renal', 'Renal'],
  ['nutritional-supplements', 'Importance of Nutritional Supplements'],
  ['weight-management', 'Weight Management'],
]

// The template asks each topic as provided or not, and beside it whether
// printed materials went with it.
const EDUCATION_QUESTIONS: TemplateQuestion[] = EDUCATION_TOPICS.flatMap(
  ([slug, topic]): TemplateQuestion[] => [
    {
      code: `edu-${slug}`,
      display: `${topic} education`,
      definition: `Nutrition education was provided on: ${topic}`,
      domain: 'education',
      capture: YES_NO,
      unmatched: NO_LOINC_EDUCATION,
    },
    {
      code: `edu-${slug}-printed-materials`,
      display: `${topic} printed materials`,
      definition: `Printed materials were provided with the ${topic} education`,
      domain: 'education',
      capture: YES_NO,
      unmatched: NO_LOINC_EDUCATION,
    },
  ]
)

const NO_LOINC_MEAL_DESCRIPTION =
  'LOINC counts meals and snacks per day (80491-4, 80486-4); it has no description of what the participant eats'

const RD_QUESTIONS: TemplateQuestion[] = [
  {
    code: 'current-labs',
    display: 'Current labs',
    definition:
      'The current laboratory results the dietitian reviewed, with their dates, as narrative',
    domain: 'nutrition',
    capture: TEXT,
    unmatched:
      'Narrative of results charted elsewhere, not a coded result; each lab has its own LOINC code',
  },
  {
    code: 'past-labs',
    display: 'Past labs',
    definition:
      'Earlier laboratory results the dietitian reviewed, with their dates, as narrative',
    domain: 'nutrition',
    capture: TEXT,
    unmatched:
      'Narrative of results charted elsewhere, not a coded result; each lab has its own LOINC code',
  },
  {
    code: 'bg-ranges',
    display: 'BG ranges',
    definition: 'The blood glucose ranges the participant checks, as narrative',
    domain: 'nutrition',
    capture: TEXT,
    unmatched:
      'LOINC glucose terms are single measurements; this is a description of the usual range',
  },
  {
    code: 'nfpe-status',
    display: 'NFPE status',
    definition:
      'Whether the nutrition focused physical exam was performed or the participant refused it',
    domain: 'nutrition',
    capture: choice('nfpe-status'),
    unmatched:
      'LOINC 75293-1 (Physical findings of nutrition assessment) is a nominal finding with an empty answer list, not whether the exam was done',
  },
  {
    code: 'nfpe-muscle',
    display: 'NFPE muscle',
    definition:
      'Muscle findings of the nutrition focused physical exam (such as wasting)',
    domain: 'nutrition',
    capture: TEXT,
    loinc: {
      code: '75293-1',
      equivalence: 'relatedto',
      comment:
        'Physical findings of nutrition assessment is one nominal item with an empty answer list; this is the muscle part, as narrative',
    },
  },
  {
    code: 'nfpe-fat',
    display: 'NFPE fat',
    definition:
      'Fat findings of the nutrition focused physical exam (such as subcutaneous fat loss)',
    domain: 'nutrition',
    capture: TEXT,
    loinc: {
      code: '75293-1',
      equivalence: 'relatedto',
      comment:
        'Physical findings of nutrition assessment is one nominal item with an empty answer list; this is the fat part, as narrative',
    },
  },
  {
    code: 'medication-types',
    display: 'Types of medications',
    definition: 'Types of medications the participant takes',
    domain: 'nutrition',
    capture: TEXT,
    unmatched:
      'LOINC medication terms are instrument items (PhenX, CMS reconciliation), not a list of types',
  },
  {
    code: 'skin-condition',
    display: "Is the participant's skin intact or are there wounds?",
    definition: "Is the participant's skin intact or are there wounds?",
    domain: 'skin',
    capture: choice('skin-condition'),
    loinc: {
      code: '80344-5',
      equivalence: 'relatedto',
      comment:
        'LOINC answers Intact or Not intact; the template offers Intact or Wounds, and wounds are one way skin is not intact',
    },
  },
  {
    code: 'wound-type',
    display: 'Wound type',
    definition:
      'The kind of wound: pressure ulcer stage I to IV, diabetic, or moisture-associated skin damage (check all that apply)',
    domain: 'skin',
    capture: choice('wound-type', { repeats: true }),
    loinc: {
      code: '76505-7',
      equivalence: 'relatedto',
      comment:
        'Highest pressure ulcer stage (NPUAP) lists the stages with unstageable and deep tissue injury, and has no diabetic or MASD answer; 72300-7 Wound type lists pressure ulcer and ulcer without a stage',
    },
  },
  {
    code: 'dentition-type',
    display: 'Dentition',
    definition:
      'The participant’s dentition: natural, dentures, partials, or edentulous (check all that apply)',
    domain: 'nutrition',
    capture: choice('dentition-type', { repeats: true }),
    unmatched:
      'LOINC 34007-5 Type of dentition is developmental (primary, mixed, permanent); 45771-3 is an MDS item on lost teeth without dentures',
  },
  {
    code: 'feeding-assist-type',
    display: 'Feeding assist type',
    definition:
      'The kind of feeding assistance: feeding, set-up, cues, reminders, or modified textures (check all that apply)',
    domain: 'nutrition',
    capture: choice('feeding-assist-type', { repeats: true }),
    unmatched:
      'LOINC has eating performance scales (CARE, CMS) and caregiver assistance items, not these kinds of assistance',
  },
  {
    code: 'appetite',
    display: 'Appetite',
    definition: 'How the participant describes their appetite',
    domain: 'nutrition',
    capture: TEXT,
    unmatched:
      'LOINC appetite terms are items of one instrument over a fixed window (PHQ-9 poor appetite, FACIT, QIDS), not a general description',
  },
  {
    code: 'breakfast-intake',
    display: 'Breakfast',
    definition: 'What the participant usually eats for breakfast',
    domain: 'nutrition',
    capture: TEXT,
    unmatched: NO_LOINC_MEAL_DESCRIPTION,
  },
  {
    code: 'lunch-intake',
    display: 'Lunch',
    definition: 'What the participant usually eats for lunch',
    domain: 'nutrition',
    capture: TEXT,
    unmatched: NO_LOINC_MEAL_DESCRIPTION,
  },
  {
    code: 'dinner-intake',
    display: 'Dinner',
    definition: 'What the participant usually eats for dinner',
    domain: 'nutrition',
    capture: TEXT,
    unmatched: NO_LOINC_MEAL_DESCRIPTION,
  },
  {
    code: 'snacks-intake',
    display: 'Snacks',
    definition: 'What the participant usually eats as snacks',
    domain: 'nutrition',
    capture: TEXT,
    loinc: {
      code: '80486-4',
      equivalence: 'relatedto',
      comment:
        'Snacks 24 hour Estimated counts snacks per day; this describes them',
    },
  },
  {
    code: 'exercise',
    display: 'Exercise',
    definition: 'Whether the participant exercises',
    domain: 'nutrition',
    capture: YES_NO,
    unmatched:
      'LOINC exercise terms are frequency or duration (74009-2) or instrument items (PROMIS 79059-2), not whether the participant exercises',
  },
  {
    code: 'exercise-details',
    display: 'Exercise details',
    definition: 'What exercise the participant does and how often',
    domain: 'nutrition',
    capture: TEXT,
    loinc: {
      code: '74009-2',
      equivalence: 'wider',
      comment:
        'Exercise duration/Exercise frequency is a quantity; this is the narrative of what and how often',
    },
  },
  {
    code: 'water-intake-notes',
    display: 'Water intake notes',
    definition: 'Notes on how much water the participant drinks',
    domain: 'nutrition',
    capture: TEXT,
    unmatched: 'LOINC fluid intake terms are measured amounts, not notes',
  },
  {
    code: 'nutritional-supplements-notes',
    display: 'Nutritional supplements notes',
    definition: 'Which nutritional supplements the participant takes and how',
    domain: 'nutrition',
    capture: TEXT,
    unmatched: 'LOINC has no list of nutritional supplements taken',
  },
  {
    code: 'housing-type',
    display: 'Housing type',
    definition: 'The type of housing the participant lives in',
    domain: 'social-history',
    capture: choice('housing-type'),
    unmatched:
      'LOINC 71802-3 Housing status is housed or homeless; 66356-7 is housing in the neighborhood (PhenX)',
  },
  {
    code: 'living-independence',
    display: 'Living independence',
    definition: 'Whether the participant lives independently or dependently',
    domain: 'social-history',
    capture: choice('living-independence'),
    unmatched:
      'LOINC has ADL and IADL performance scales, not a living independence status',
  },
  {
    code: 'dependent-living-setting',
    display: 'Dependent living setting',
    definition:
      'Where a participant who lives dependently lives: nursing home, assisted living, or board and care',
    domain: 'social-history',
    capture: choice('dependent-living-setting'),
    unmatched:
      'LOINC 71802-3 Housing status and 63725-6 Residence do not list these settings',
  },
  {
    code: 'other-food-resources',
    display: 'Other food resources',
    definition: 'Whether the participant uses other food resources',
    domain: 'nutrition',
    capture: YES_NO,
    unmatched:
      'LOINC 98888-1 and 98879-0 are nutrition assistance received since the pandemic began (a dated window), not current use',
  },
  {
    code: 'food-stamps-amount',
    display: 'Food stamps amount',
    definition: 'Food stamps received, in dollars per month',
    domain: 'nutrition',
    capture: { kind: 'quantity', unit: '{USD}/mo' },
    unmatched: 'LOINC has no food stamp amount term',
  },
  {
    code: 'other-food-resources-description',
    display: 'Other food resources described',
    definition:
      'Other food resources the participant uses, in the dietitian’s words',
    domain: 'nutrition',
    capture: TEXT,
    unmatched: 'LOINC has no list of food resources used',
  },
  ...EDUCATION_QUESTIONS,
]

export const RD_QUESTION_CONCEPTS = RD_QUESTIONS.map(templateQuestionConcept)

export const RD_QUESTION_LOINC_MAP: ConceptMapGroupElement[] = RD_QUESTIONS.map(
  templateQuestionLoincMap
)

function answerSet(
  name: string,
  title: string,
  codes: AnswerEntry[]
): ValueSet {
  return answerValueSet(
    name,
    title,
    ASSESSMENT_ANSWERS_SYSTEM,
    codes.map(([code, display]) => ({ code, display }))
  )
}

export const RD_ANSWER_SETS: ValueSet[] = [
  answerSet('nfpe-status', 'Nutrition focused physical exam', [
    ['nfpe-performed', 'Performed'],
    ['nfpe-refused', 'Refused'],
  ]),
  answerSet('skin-condition', 'Skin condition', [
    ['intact', 'Intact'],
    ['skin-wounds', 'Wounds'],
  ]),
  answerSet('wound-type', 'Wound type', [
    ['pressure-ulcer-stage-1', 'PU I'],
    ['pressure-ulcer-stage-2', 'PU II'],
    ['pressure-ulcer-stage-3', 'PU III'],
    ['pressure-ulcer-stage-4', 'PU IV'],
    ['diabetic-wound', 'Diabetic'],
    ['masd', 'MASD'],
  ]),
  answerSet('dentition-type', 'Dentition', [
    ['dentition-natural', 'Natural'],
    ['dentition-dentures', 'Dentures'],
    ['dentition-partials', 'Partials'],
    ['dentition-edentulous', 'Edentulous'],
  ]),
  answerSet('feeding-assist-type', 'Feeding assist type', [
    ['feeding-assist-feeding', 'Feeding'],
    ['feeding-assist-set-up', 'Set-up'],
    ['feeding-assist-cues', 'Cues'],
    ['feeding-assist-reminders', 'Reminders'],
    ['feeding-assist-modified-textures', 'Modified Textures'],
  ]),
  answerSet('housing-type', 'Housing type', [
    ['housing-house', 'House'],
    ['housing-apartment', 'Apartment'],
    ['housing-with-family', 'With family'],
    ['housing-rents-room', 'Rents room'],
  ]),
  answerSet('living-independence', 'Living independence', [
    ['independent', 'Independent'],
    ['living-dependent', 'Dependent'],
  ]),
  answerSet('dependent-living-setting', 'Dependent living setting', [
    ['nursing-home', 'Nursing home'],
    ['assisted-living', 'AL'],
    ['board-and-care', 'B and C'],
  ]),
]

// Intact and Independent are base answer codes other sets already use.
export const RD_ANSWER_CONCEPTS = answerConcepts(
  RD_ANSWER_SETS.flatMap((set) =>
    (set.compose?.include ?? []).flatMap((include) =>
      (include.concept ?? []).map(({ code, display }): AnswerEntry => [
        code,
        display ?? code,
      ])
    )
  ),
  { reused: new Set(['intact', 'independent']), displays: 'unique' }
)
